'use client';

import { c as _c } from "react/compiler-runtime";
import type { FollowUpsPromptContext } from '@/app/(chat)/api/chat/follow-ups/prompt';
import type { AiContext } from '@/components/chat/types/ai-context';
import type { SidebarChatMessage } from '@/components/chat/types/sidebar-chat-message';
import type { LlmFollowUpSuggestion } from '@/data/chat/chat';
import { useQuery } from '@tanstack/react-query';
import { getMessageTextContent, isFocusCodePart } from '@/components/chat/message-part-utils';
import { chatFollowUpsQueryKey } from '@/data/chat/query-keys';
import { getReviewDecisionFilenames } from '@/lib/pr-v2/file-review-decisions';
const MAX_MESSAGE_TAIL_LENGTH = 2_000;
const MAX_REMAINING_FILES = 10;
function truncateTail(value: string, maxLength: number): string | undefined {
  const normalized = value.trim();
  if (!normalized) {
    return undefined;
  }
  if (normalized.length <= maxLength) {
    return normalized;
  }

  // Keep the END of the text: with long answers the conclusion carries the
  // open concerns a follow-up should push on.
  return normalized.slice(-maxLength);
}
type BuildChatFollowUpsContextArgs = {
  messages: SidebarChatMessage[];
  aiContext: AiContext;
  viewedFiles: string[];
};
export function buildChatFollowUpsContext({
  messages,
  aiContext,
  viewedFiles
}: BuildChatFollowUpsContextArgs): FollowUpsPromptContext {
  const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
  const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
  const focusedFilenames = (lastAssistantMessage?.parts ?? []).filter(isFocusCodePart).map(part => {
    const input = part.input as {
      filename?: string;
    } | undefined;
    return input?.filename;
  }).filter((filename): filename is string => Boolean(filename));
  const viewed = new Set(viewedFiles);
  const reviewDecisionFilenames = getReviewDecisionFilenames(aiContext.reviewDecisions);
  const remainingLines = (aiContext.orderedFiles ?? []).filter(file => !viewed.has(file.filename)).slice(0, MAX_REMAINING_FILES).map(file => {
    return reviewDecisionFilenames.has(file.filename) ? `- ${file.filename} (decision)` : `- ${file.filename}`;
  });
  return {
    prTitle: aiContext.pr.title,
    lastUserMessage: lastUserMessage ? truncateTail(getMessageTextContent(lastUserMessage), 1_500) : undefined,
    lastAssistantMessage: lastAssistantMessage ? truncateTail(getMessageTextContent(lastAssistantMessage), MAX_MESSAGE_TAIL_LENGTH) : undefined,
    focusedFilenames,
    remainingFilesSummary: remainingLines.length > 0 ? remainingLines.join('\n') : undefined
  };
}
type FetchChatFollowUpsParams = {
  context: FollowUpsPromptContext;
  installationId: number;
  prNumber: number;
  chatId: string;
  lastAssistantMessageId: string;
  signal?: AbortSignal;
};
export const fetchChatFollowUps = async ({
  signal,
  ...body
}: FetchChatFollowUpsParams): Promise<LlmFollowUpSuggestion[]> => {
  const response = await fetch('/api/chat/follow-ups', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      context: body.context,
      cacheKeyData: {
        installationId: body.installationId,
        prNumber: body.prNumber,
        chatId: body.chatId,
        lastAssistantMessageId: body.lastAssistantMessageId
      }
    }),
    signal
  });
  if (!response.ok) {
    throw new Error('Failed to fetch chat follow-ups');
  }
  const data = await response.json();
  return data.suggestions ?? [];
};
type UseChatFollowUpsParams = {
  chatId: string;
  messages: SidebarChatMessage[];
  aiContext: AiContext;
  viewedFiles: string[];
  installationId: number | null;
  prNumber: number;
};
export const useChatFollowUps = (params, options) => {
  const $ = _c(21);
  const {
    chatId,
    messages,
    aiContext,
    viewedFiles,
    installationId,
    prNumber
  } = params;
  let isEnabled;
  let lastAssistantMessageId;
  let t0;
  let t1;
  if ($[0] !== chatId || $[1] !== installationId || $[2] !== messages || $[3] !== options?.enabled || $[4] !== prNumber) {
    const lastMessage = messages.at(-1);
    lastAssistantMessageId = lastMessage?.role === "assistant" ? lastMessage.id : null;
    isEnabled = Boolean((options?.enabled ?? true) && installationId && prNumber && lastAssistantMessageId);
    t1 = useQuery;
    t0 = chatFollowUpsQueryKey(chatId, lastAssistantMessageId ?? "none");
    $[0] = chatId;
    $[1] = installationId;
    $[2] = messages;
    $[3] = options?.enabled;
    $[4] = prNumber;
    $[5] = isEnabled;
    $[6] = lastAssistantMessageId;
    $[7] = t0;
    $[8] = t1;
  } else {
    isEnabled = $[5];
    lastAssistantMessageId = $[6];
    t0 = $[7];
    t1 = $[8];
  }
  let t2;
  if ($[9] !== aiContext || $[10] !== chatId || $[11] !== installationId || $[12] !== lastAssistantMessageId || $[13] !== messages || $[14] !== prNumber || $[15] !== viewedFiles) {
    t2 = t3 => {
      const {
        signal
      } = t3;
      return fetchChatFollowUps({
        context: buildChatFollowUpsContext({
          messages,
          aiContext,
          viewedFiles
        }),
        installationId: installationId ?? 0,
        prNumber,
        chatId,
        lastAssistantMessageId: lastAssistantMessageId ?? "none",
        signal
      });
    };
    $[9] = aiContext;
    $[10] = chatId;
    $[11] = installationId;
    $[12] = lastAssistantMessageId;
    $[13] = messages;
    $[14] = prNumber;
    $[15] = viewedFiles;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  let t3;
  if ($[17] !== isEnabled || $[18] !== t0 || $[19] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t2,
      enabled: isEnabled,
      staleTime: Infinity,
      retry: false,
      placeholderData: _temp
    };
    $[17] = isEnabled;
    $[18] = t0;
    $[19] = t2;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  return t1(t3);
};
function _temp() {}